// WbDriversPage — /weighbridge/drivers (admin only).
//
// Phase C3 Group 3. Lists the tenant's weighbridge drivers from
// /api/v1/wb/drivers. Server-side paginated and searchable by name.
// Row click navigates to the detail/edit page; "+ Add Driver" routes
// to /new. WB drivers are a SEPARATE party entity from the collections
// /admin/drivers list (wb_drivers vs the legacy drivers table).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, IdCard } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useWbDrivers } from '../hooks/useWbDrivers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbDriversPage.module.css';

const PER_PAGE = 50;

export function WbDriversPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search input — 300ms feels responsive without spamming
  // the server. Reset to page 1 whenever the search term changes.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const params = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
    }),
    [page, debouncedSearch],
  );

  const driversQuery = useWbDrivers(params);
  const drivers = driversQuery.data?.drivers ?? [];
  const pagination = driversQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const hasNoSearch = debouncedSearch === '';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Drivers"
        subtitle="Drivers used for weighbridge loads"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/drivers/new')}
          >
            Add Driver
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search drivers by name"
        />
      </div>

      {driversQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(driversQuery.error)}</p>
      ) : null}

      <Card flush>
        {driversQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading drivers" />
          </div>
        ) : drivers.length === 0 ? (
          hasNoSearch ? (
            <EmptyState
              icon={IdCard}
              title="No drivers yet"
              description="Add a driver to start recording who's behind the wheel on each load."
              action={
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/weighbridge/drivers/new')}
                >
                  Add Driver
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={IdCard}
              title="No matching drivers"
              description={`No drivers match "${debouncedSearch}".`}
            />
          )
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Default haulier</th>
                  <th>Licence #</th>
                  <th>Phone</th>
                  <th>Signature on file</th>
                  <th>Active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <tr
                    key={d.enc_id}
                    onClick={() => navigate(`/weighbridge/drivers/${d.enc_id}`)}
                  >
                    <td>{d.name}</td>
                    <td>
                      {d.default_haulier ? (
                        d.default_haulier.name
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {d.licence_number ?? (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {d.phone ?? <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      {d.signature_image_url ? (
                        <Badge variant="success">Yes</Badge>
                      ) : (
                        <Badge variant="muted">No</Badge>
                      )}
                    </td>
                    <td>
                      {d.is_active ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="muted">Inactive</Badge>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/weighbridge/drivers/${d.enc_id}`);
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && totalPages > 1 ? (
              <div className={styles.pagination}>
                <span>
                  Page {pagination.page} of {totalPages} ({pagination.total} total)
                </span>
                <div className={styles.pageControls}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
